import { useRef, useState, useEffect, useMemo } from "react";
import { motion, useAnimation } from "framer-motion";
import {
  Settings,
  TrendingUp,
  LayoutGrid,
  Smile,
  FileText,
  Sparkles,
  Shield,
  Wrench,
  CalendarClock,
} from "lucide-react";

import Section from "@/components/layout/Section";
import Container from "@/components/layout/Container";
import Stack from "@/components/layout/Stack";
import { fadeUp } from "@/utils/motion";

/* ================= CONFIG ================= */

const FALLBACK =
  "https://res.cloudinary.com/demo/image/upload/v1312461204/sample.jpg";

/* ================= IMAGE UTILS ================= */

const getImage = (url) => {
  if (!url || typeof url !== "string") return FALLBACK;
  return url; // 🔥 SVG CDN safe
};

/* ================= DATA ================= */

const services = [
  {
    icon: Settings,
    title: "Hands Off Operations",
    text: "We manage daily operations, guest handling, and upkeep to keep your property running smoothly.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776758659/full_service_mangement_pnvv4h.svg",
  },
  {
    icon: TrendingUp,
    title: "Market Driven Pricing",
    text: "Rates adjust using demand signals, seasonality, and local trends to maximise revenue.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776758658/Market_Driven_Pricing_hnns9m.svg",
  },
  {
    icon: LayoutGrid,
    title: "High Conversion Listings",
    text: "Listings are designed with strong visuals and copy to improve clicks and booking intent.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660976/High_Conversion_Listings_tyuuwd.svg",
  },
  {
    icon: Smile,
    title: "Guest Experience",
    text: "Clear communication and fast support create better stays and consistently stronger reviews.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660973/Guest_exp_zv67uv.svg",
  },
  {
    icon: CalendarClock,
    title: "Booking Control",
    text: "Availability is managed to reduce gaps, avoid conflicts, and maintain steady occupancy.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660970/Booking_Control_f6jshk.svg",
  },
  {
    icon: Shield,
    title: "Quality & Compliance",
    text: "Standards and checks ensure your property stays consistent, compliant, and guest ready.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660974/Quality_compliance_ueyqxb.svg",
  },
  {
    icon: Wrench,
    title: "Maintenance Coordination",
    text: "Repairs and cleaning are handled quickly to minimize downtime and maintain property quality.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660973/Home_Mainte_xsaaco.svg",
  },
  {
    icon: FileText,
    title: "Owner Reporting",
    text: "Clear reports show bookings, revenue trends, and overall performance in one place.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660979/home_owner_report_ypajav.svg",
  },
  {
    icon: Sparkles,
    title: "Design Optimisation",
    text: "Interior upgrades improve guest experience, increase appeal, and support better pricing.",
    image: "https://res.cloudinary.com/dge0knh7q/image/upload/v1776660976/interior_design_uqhxm1.svg",
  },
];

/* ================= IMAGE CARD ================= */

function ImageCard({ item, priority }) {
  const [loaded, setLoaded] = useState(false);

  // 🔥 preload image
  useEffect(() => {
    const img = new Image();
    img.src = item.image;
  }, [item.image]);

  return (
    <div
      className="
        min-w-[260px]
        sm:min-w-[300px]
        md:min-w-[340px]
        lg:min-w-[380px]
        rounded-[24px] overflow-hidden
        bg-bg-light
        ring-1 ring-border-soft
      "
    >
      <div className="relative aspect-[4/3]">

        {/* Skeleton */}
        {!loaded && (
          <div className="absolute inset-0 animate-pulse bg-white/10" />
        )}

        <img
          src={getImage(item.image)}
          alt={item.title}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={(e) => (e.currentTarget.src = FALLBACK)}
          className={`
            absolute inset-0 w-full h-full object-cover
            transition-opacity duration-300
            ${loaded ? "opacity-100" : "opacity-0"}
          `}
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 p-4">
          <span className="text-white text-[14px] font-semibold">
            {item.title}
          </span>
        </div>

      </div>
    </div>
  );
}

/* ================= IMAGE RAIL ================= */

function ImageRail() {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const controls = useAnimation();

  const [dragging, setDragging] = useState(false);
  const [maxDrag, setMaxDrag] = useState(0);

  const loop = useMemo(() => [...services, ...services], []);

  /* Width calc */
  useEffect(() => {
    const calc = () => {
      if (!trackRef.current || !containerRef.current) return;
      setMaxDrag(
        trackRef.current.scrollWidth - containerRef.current.offsetWidth
      );
    };

    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  /* Auto scroll */
  useEffect(() => {
    if (maxDrag === 0) return;

    if (!dragging) {
      controls.start({
        x: [0, -maxDrag / 2],
        transition: {
          duration: 30,
          ease: "linear",
          repeat: Infinity,
        },
      });
    } else {
      controls.stop();
    }
  }, [dragging, maxDrag]);

  return (
    <div ref={containerRef} className="relative overflow-hidden">

      {/* Fade edges */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-bg-surface to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-bg-surface to-transparent z-10" />

      <motion.div
        ref={trackRef}
        className="flex gap-4 cursor-grab active:cursor-grabbing"
        style={{ touchAction: "pan-y" }}

        drag="x"
        dragConstraints={{ left: -maxDrag, right: 0 }}
        dragElastic={0.08}
        dragMomentum

        animate={controls}

        onPointerDown={() => {
          setDragging(true);
          controls.stop();
        }}
        onPointerUp={() => setDragging(false)}
        onPointerLeave={() => setDragging(false)}
        onDragEnd={() => setDragging(false)}
      >
        {loop.map((item, i) => (
          <ImageCard
            key={i}
            item={item}
            priority={i < 2} // preload first few
          />
        ))}
      </motion.div>
    </div>
  );
}

/* ================= MAIN ================= */

export default function SolutionsGrid() {
  return (
    <Section background="bg-bg-surface" padding="lg">
      <Container>

        <Stack gap="lg">

          {/* HEADER */}
          <header className="flex max-w-content flex-col text-left">
            <span className="text-small font-semibold uppercase tracking-wide text-brand-primary">
              Solutions
            </span>

            <h2 className="mt-4 text-display font-extrabold text-text-primary max-w-[18ch]">
              Everything we manage end to end
            </h2>

            <p className="mt-5 max-w-reading text-body text-text-muted">
              Zenia manages every part of your rental through a structured system.
            </p>
          </header>

          {/* IMAGE RAIL */}
          <ImageRail />

          {/* GRID */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
            {services.map((item, index) => (
              <motion.div
                key={index}
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                whileHover={{ y: -3 }}
                transition={{ duration: 0.2 }}
                className="
                  group flex h-full min-h-[180px] flex-col rounded-[24px]
                  border border-border-soft bg-white p-4
                  shadow-soft transition-all duration-300
                  hover:shadow-card
                  md:min-h-[220px] md:p-6
                "
              >
                <Stack gap="sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-bg-light">
                    <item.icon size={18} className="text-text-primary" />
                  </div>

                  <strong className="text-text-primary">
                    {item.title}
                  </strong>

                  <p className="text-[13px] md:text-body text-text-muted leading-relaxed">
                    {item.text}
                  </p>
                </Stack>
              </motion.div>
            ))}
          </div>

        </Stack>

      </Container>
    </Section>
  );
}